import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, ClockIcon, HomeIcon, MailCheckIcon, GlobeIcon, UsersIcon, StarIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const FLYER_IMAGE = 'https://cdn.magicpatterns.com/uploads/ewJpinVHQ8s4RJSdCh5B3M/HAA_Sep_15.png';
const CONTACT_PHONE = '703-313-1465';
const CONTACT_EMAIL = 'info@honduranamerican.org';
const NAVY = '#0b2148';
const RED = '#96222a';
const DETAIL_ICONS = [CalendarIcon, ClockIcon, HomeIcon];
const COPY = {
  EN: {
    back: 'Back to site',
    eyebrow: 'Hosted by the Honduran-American Association',
    script: 'Honduran',
    title: 'Independence Day',
    subtitle: 'Commemorating 205 years of Honduran independence, and honoring the heritage, contributions, and enduring spirit of the Honduran-American community.',
    details: [{
      label: 'Date',
      value: 'Tuesday, September 15'
    }, {
      label: 'Time',
      value: '6:00 – 8:30 PM'
    }, {
      label: 'Location',
      value: 'At the home of the President of the Honduran-American Association'
    }],
    flyerAria: 'View the full invitation graphic',
    flyerAlt: 'Honduran Independence Day — Tuesday, September 15, 6:00 to 8:30 PM, at the home of the President of the Honduran-American Association. RSVP required.',
    rsvpBadge: 'Registration Closed',
    rsvpTitle: 'We Have Reached Capacity',
    rsvpText: 'Registration for this celebration is now closed. We are grateful for the extraordinary response from across the community.',
    rsvpHighlight: 'Over 70 leaders from international organizations, the Embassy of Honduras, the business community, U.S. government agencies, and think tanks will join us for a special evening.',
    rsvpNote: 'Confirmed guests will receive the venue address by email in advance of the celebration.',
    contactLead: 'For inquiries, please contact',
    aboutEyebrow: 'About the Evening',
    aboutHeading: 'An Evening of Heritage and Community',
    aboutParagraphs: ['Each September 15, Hondurans throughout the world commemorate the anniversary of our nation’s independence. This year, the Honduran-American Association is honored to host an intimate reception at the residence of its President in observance of 205 years of Honduran sovereignty.', 'Guests will join leaders, professionals, families, and friends of the Honduran-American community for an evening of meaningful conversation, cultural tradition, and shared appreciation for our common heritage.', 'The evening is both a celebration of that heritage and an invitation to strengthen the relationships that continue to advance our community across the United States.'],
    highlights: [{
      title: 'A National Commemoration',
      text: 'Two centuries of independence observed through the traditions, music, and cuisine that connect our community to Honduras.'
    }, {
      title: 'An Intimate Reception',
      text: 'A gathering at the residence of the Association’s President, designed for genuine conversation and personal connection.'
    }, {
      title: 'Community Convened',
      text: 'Leaders, families, and friends of the Honduran-American community brought together for a single evening of fellowship.'
    }],
    missionHeading: 'Connect. Empower. Celebrate.',
    missionText: 'The mission of the Honduran-American Association is to Connect, Empower, and Celebrate. Independence Day represents that mission in its fullest expression — an occasion to honor our shared history while recognizing the community shaping its future.',
    missionClosing: '¡Viva Honduras!',
    footer: 'Hosted by the Honduran-American Association'
  },
  ES: {
    back: 'Volver al sitio',
    eyebrow: 'Organizado por la Asociación Hondureño-Americana',
    script: 'Día de la',
    title: 'Independencia de Honduras',
    subtitle: 'Conmemorando 205 años de independencia hondureña, y honrando la herencia, las contribuciones y el espíritu perdurable de la comunidad hondureño-americana.',
    details: [{
      label: 'Fecha',
      value: 'Martes, 15 de septiembre'
    }, {
      label: 'Hora',
      value: '6:00 – 8:30 P.M.'
    }, {
      label: 'Lugar',
      value: 'En la casa del Presidente de la Asociación Hondureño-Americana'
    }],
    flyerAria: 'Ver la invitación completa',
    flyerAlt: 'Día de la Independencia de Honduras — Martes 15 de septiembre, 6:00 a 8:30 P.M., en la casa del Presidente de la Asociación Hondureño-Americana. Se requiere confirmación.',
    rsvpBadge: 'Inscripciones Cerradas',
    rsvpTitle: 'Hemos Alcanzado el Cupo',
    rsvpText: 'Las inscripciones para esta celebración ya están cerradas. Agradecemos profundamente la extraordinaria respuesta de toda la comunidad.',
    rsvpHighlight: 'Más de 70 líderes de organizaciones internacionales, la Embajada de Honduras, el sector empresarial, agencias del gobierno de los Estados Unidos y centros de pensamiento nos acompañarán en una velada especial.',
    rsvpNote: 'Los invitados confirmados recibirán la dirección del lugar por correo electrónico antes de la celebración.',
    contactLead: 'Para consultas, favor contactar',
    aboutEyebrow: 'Sobre la Noche',
    aboutHeading: 'Una Noche de Herencia y Comunidad',
    aboutParagraphs: ['Cada 15 de septiembre, los hondureños alrededor del mundo conmemoran el aniversario de la independencia de nuestra nación. Este año, la Asociación Hondureño-Americana tiene el honor de ofrecer una recepción íntima en la residencia de su Presidente en observancia de 205 años de soberanía hondureña.', 'Los invitados se reunirán junto a líderes, profesionales, familias y amigos de la comunidad hondureño-americana para una velada de conversación significativa, tradición cultural y aprecio compartido por nuestra herencia común.', 'La velada es a la vez una celebración de esa herencia y una invitación a fortalecer las relaciones que continúan impulsando a nuestra comunidad en los Estados Unidos.'],
    highlights: [{
      title: 'Una Conmemoración Nacional',
      text: 'Dos siglos de independencia observados a través de las tradiciones, la música y la gastronomía que conectan a nuestra comunidad con Honduras.'
    }, {
      title: 'Una Recepción Íntima',
      text: 'Un encuentro en la residencia del Presidente de la Asociación, concebido para la conversación genuina y la conexión personal.'
    }, {
      title: 'Comunidad Convocada',
      text: 'Líderes, familias y amigos de la comunidad hondureño-americana reunidos para una velada de confraternidad.'
    }],
    missionHeading: 'Conectar. Empoderar. Celebrar.',
    missionText: 'La misión de la Asociación Hondureño-Americana es Conectar, Empoderar y Celebrar. El Día de la Independencia representa esa misión en su máxima expresión: una ocasión para honrar nuestra historia compartida y reconocer a la comunidad que construye su futuro.',
    missionClosing: '¡Viva Honduras!',
    footer: 'Organizado por la Asociación Hondureño-Americana'
  }
};
export function HonduranIndependenceDayPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isEs = language === 'ES';
  const c = isEs ? COPY.ES : COPY.EN;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen w-full bg-[#e6e1d5] font-nunito" data-id="element-3662" data-mp="2aw0tib61zlpr">
      {/* Flag stripe */}
      <div className="w-full" data-id="element-3663" data-mp="2axvhxzgj9vyl">
        <div className="h-1.5" style={{
        backgroundColor: NAVY
      }} data-id="element-3664" data-mp="2fulrs51qvvbk" />
        <div className="h-1 bg-white" data-id="element-3665" data-mp="2ftatuluzdhsd" />
        <div className="h-1.5" style={{
        backgroundColor: RED
      }} data-id="element-3666" data-mp="2kugmyksjz72f" />
      </div>

      <header className="w-full px-6 py-5 sm:px-12 flex items-center justify-between gap-4 bg-white border-b border-[#12336b]/10" data-id="element-3667" data-mp="lhciad9789tb">
        <a href="#" className="flex items-center gap-3 group" aria-label="Honduran American Association" data-id="element-3668" data-mp="qh8p0k9a6fck">
          <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="h-10 w-auto object-contain group-hover:opacity-80 transition-opacity" data-id="element-3669" data-mp="2sbd4n95i1h18" />
        </a>
        <div className="flex items-center gap-3" data-id="element-3670" data-mp="2x6izwyksk81z">
          <button onClick={() => setLanguage(isEs ? 'EN' : 'ES')} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-bold transition-colors hover:opacity-90" style={{
          backgroundColor: RED
        }} aria-label={isEs ? 'Switch to English' : 'Ver en español'} data-id="element-3671" data-mp="29e27bu3fgxof">
            <GlobeIcon className="w-4 h-4" data-id="element-3672" data-mp="2eg1c98etg2nh" />
            {isEs ? 'English' : 'Español'}
          </button>
          <a href="#" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold transition-colors hover:bg-[#12336b]/5" style={{
          borderColor: `${NAVY}33`,
          color: NAVY
        }} data-id="element-3673" data-mp="3uu16q3uw703c">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-3674" data-mp="67d09n2ww9q5" />
            <span className="hidden sm:inline" data-id="element-3675" data-mp="63ywqbiihuh4">{c.back}</span>
          </a>
        </div>
      </header>

      <main data-id="element-3676" data-mp="3sdjmdxmnwntd">
        {/* Title */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pt-12 lg:pt-16" data-id="element-3677" data-mp="3scrscr8pqy57">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.32, 1]
        }} className="text-center max-w-3xl mx-auto" data-id="element-3678" data-mp="jx81tzzffttu">
            <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4" style={{
            color: RED
          }} data-id="element-3679" data-mp="b562k513gjl5">
              {c.eyebrow}
            </p>
            <p className="font-nunito italic text-2xl sm:text-3xl mb-1" style={{
            color: NAVY
          }} data-id="element-3680" data-mp="12vsqllduin4b">
              {c.script}
            </p>
            <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-[0.06em] leading-[0.95]" style={{
            color: NAVY
          }} data-id="element-3681" data-mp="uj6dfvn6yyx5">
              {c.title}
            </h1>
            <div className="flex items-center justify-center gap-2 mt-5 mb-5" data-id="element-3682" data-mp="kkgwc534dihy">
              {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="w-3.5 h-3.5" style={{
              color: NAVY
            }} fill={NAVY} data-id="element-3683" data-mp="piii4xrxjvx4" />)}
            </div>
            <p className="text-base sm:text-lg leading-relaxed" style={{
            color: `${NAVY}cc`
          }} data-id="element-3684" data-mp="glhxn27zehyy">
              {c.subtitle}
            </p>
          </motion.div>

          {/* Info bar */}
          <dl className="mt-10 grid sm:grid-cols-3 gap-px rounded-2xl overflow-hidden border border-[#12336b]/15 bg-[#12336b]/15" data-id="element-3685" data-mp="3oyyn8eeku8qt">
            {c.details.map((item, i) => {
            const Icon = DETAIL_ICONS[i];
            return <div key={item.label} className="bg-white px-5 py-5 flex items-start gap-3" data-id="element-3686" data-mp="3ad1khqpnrzca" data-mpi={i}>
                  <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{
                color: RED
              }} data-id="element-3687" data-mp="3jdzdldyk6mg3" />
                  <div data-id="element-3688" data-mp="3ectmtlogl997">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.18em] mb-0.5" style={{
                  color: `${NAVY}b3`
                }} data-id="element-3689" data-mp="3egpq43uqckuw">
                      {item.label}
                    </dt>
                    <dd className="text-sm font-semibold leading-snug" style={{
                  color: NAVY
                }} data-id="element-3690" data-mp="5xi8ek3h167z">
                      {item.value}
                    </dd>
                  </div>
                </div>;
          })}
          </dl>
        </section>

        {/* Flyer + RSVP */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12 lg:py-16" data-id="element-3691" data-mp="34pmeuti7mjgb">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-stretch" data-id="element-3692" data-mp="34njhfoeq9o1k">
            <motion.aside initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.16, 1, 0.32, 1]
          }} className="flex" data-id="element-3693" data-mp="38rr8hs8nrg2y">
              <a href={FLYER_IMAGE} target="_blank" rel="noopener noreferrer" className="relative block w-full h-full rounded-2xl overflow-hidden shadow-xl ring-1 bg-white group" style={{
              boxShadow: '0 20px 50px -20px rgba(18,51,107,0.35)'
            }} aria-label={c.flyerAria} data-id="element-3694" data-mp="3ng42wks3kqvc">
                <img src={FLYER_IMAGE} alt={c.flyerAlt} className="w-full h-full object-contain block transition-transform duration-500 group-hover:scale-[1.02]" data-id="element-3695" data-mp="58emcpc4df7f" />
              </a>
            </motion.aside>

            <motion.div initial={{
            opacity: 0,
            y: 24
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1,
            ease: [0.16, 1, 0.32, 1]
          }} className="flex flex-col h-full" data-id="element-3696" data-mp="1zjxl9qpw7900">
              <div className="rounded-2xl border border-[#12336b]/15 bg-white overflow-hidden flex flex-col flex-1 shadow-sm" data-id="element-3697" data-mp="1ktyptvpq2bo8">
                <div className="px-6 pt-6 pb-4" data-id="element-3698" data-mp="2ifw6dnkjk6xj">
                  <span className="inline-block text-[10px] font-bold uppercase tracking-[0.22em] px-3 py-1 rounded-full text-white mb-3" style={{
                  backgroundColor: RED
                }} data-id="element-3699" data-mp="2di4bckolo4p4">
                    {c.rsvpBadge}
                  </span>
                  <h2 className="inline-flex items-center gap-2 font-bebas text-2xl tracking-wider mb-2" style={{
                  color: NAVY
                }} data-id="element-3700" data-mp="24jsbds1ufuk0">
                    <MailCheckIcon className="w-5 h-5" style={{
                    color: RED
                  }} data-id="element-3701" data-mp="t4df6qhgl6py" />
                    {c.rsvpTitle}
                  </h2>
                  <p className="text-sm leading-relaxed" style={{
                  color: `${NAVY}b3`
                }} data-id="element-3702" data-mp="2lq75x2faagmc">
                    {c.rsvpText}
                  </p>
                </div>

                <div className="relative flex-1 bg-[#e6e1d5] border-y border-[#0b2148]/20 flex items-center overflow-hidden" data-id="element-3703" data-mp="2c0nlhsbxaepd">
                  <img src="https://cdn.magicpatterns.com/uploads/iPHjEpGidZAqG1gMfpLmLH/1c7c57f7-be5c-44b0-8ce9-1416397b2473.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-[0.13]" data-id="element-3704" data-mp="2zq9luj416zyy" />
                  <div className="absolute inset-0" style={{
                  background: 'linear-gradient(180deg, rgba(230,225,213,0.85) 0%, rgba(230,225,213,0.6) 50%, rgba(230,225,213,0.9) 100%)'
                }} data-id="element-3705" data-mp="1f9msj2sv9hce" />
                  <div className="relative w-full px-8 py-12 text-center" data-id="element-3706" data-mp="16t47dcc8xe5i">
                    <div className="w-14 h-14 mx-auto mb-6 rounded-full flex items-center justify-center" style={{
                    backgroundColor: `${NAVY}12`
                  }} data-id="element-3707" data-mp="16uyr4nbs7080">
                      <UsersIcon className="w-7 h-7" style={{
                      color: NAVY
                    }} data-id="element-3708" data-mp="t0y2wniggvlw" />
                    </div>
                    <p className="font-bebas text-5xl sm:text-6xl tracking-wider leading-none mb-1" style={{
                    color: RED
                  }} data-id="element-3709" data-mp="o2d2oo37ebxg">
                      70+
                    </p>
                    <p className="text-[11px] font-bold uppercase tracking-[0.22em] mb-6" style={{
                    color: `${NAVY}99`
                  }} data-id="element-3710" data-mp="xziujyjn66yq">
                      {isEs ? 'Líderes Confirmados' : 'Confirmed Leaders'}
                    </p>
                    <p className="text-base leading-relaxed max-w-md mx-auto" style={{
                    color: NAVY
                  }} data-id="element-3711" data-mp="uyppzte3kb4k">
                      {c.rsvpHighlight}
                    </p>
                    <div className="w-12 h-px mx-auto my-6" style={{
                    backgroundColor: `${NAVY}26`
                  }} data-id="element-3712" data-mp="13sujkkkm80ns" />
                    <p className="text-sm leading-relaxed max-w-sm mx-auto" style={{
                    color: `${NAVY}b3`
                  }} data-id="element-3713" data-mp="c1y7vnaak223">
                      {c.rsvpNote}
                    </p>
                  </div>
                </div>

                <div className="px-6 py-4" data-id="element-3714" data-mp="q17d5rwylbra">
                  <p className="text-sm leading-relaxed" style={{
                  color: `${NAVY}99`
                }} data-id="element-3715" data-mp="q28xkmzv7alo">
                    {c.contactLead}{' '}
                    <a href={`tel:${CONTACT_PHONE.replace(/-/g, '')}`} className="font-semibold hover:underline" style={{
                    color: RED
                  }} data-id="element-3716" data-mp="21funwbhgni8s">
                      {CONTACT_PHONE}
                    </a>
                    {' · '}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold hover:underline" style={{
                    color: RED
                  }} data-id="element-3717" data-mp="3c7spmkqftv8k">
                      {CONTACT_EMAIL}
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* About */}
        <section className="border-t border-[#12336b]/10" style={{
        backgroundColor: '#ffffff'
      }} data-id="element-3718" data-mp="2t8okvo5xinmp">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-24" data-id="element-3719" data-mp="2obeti2yzc1w8">
            <div className="text-center max-w-3xl mx-auto" data-id="element-3720" data-mp="rgs707wah1or">
              <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4" style={{
              color: RED
            }} data-id="element-3721" data-mp="miqnxmyv52da">
                {c.aboutEyebrow}
              </p>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-wider leading-tight mb-4" style={{
              color: NAVY
            }} data-id="element-3722" data-mp="chewgfjszd4">
                {c.aboutHeading}
              </h2>
              <div className="w-16 h-px mx-auto mb-8" style={{
              backgroundColor: RED
            }} data-id="element-3723" data-mp="3mrk5dd7hs8pp" />
            </div>
            <div className="space-y-5 text-base sm:text-lg leading-relaxed text-center max-w-3xl mx-auto" style={{
            color: `${NAVY}cc`
          }} data-id="element-3724" data-mp="38udmlimrqa0h">
              {c.aboutParagraphs.map(text => <p key={text} data-id="element-3725" data-mp="33sxzzm4xzvfz">{text}</p>)}
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-14" data-id="element-3726" data-mp="33vaiq1iiy9u3">
              {c.highlights.map((item, i) => <motion.div key={item.title} initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.1,
              ease: [0.16, 1, 0.32, 1]
            }} className="rounded-2xl border border-[#0b2148]/20 bg-[#e6e1d5] p-7 text-center" data-id="element-3727" data-mp="121uo6tjbgatu" data-mpi={i}>
                  <div className="w-10 h-px mb-5 mx-auto" style={{
                backgroundColor: RED
              }} data-id="element-3728" data-mp="367megfadvrj0" />
                  <h3 className="font-bebas text-2xl tracking-wider mb-3" style={{
                color: NAVY
              }} data-id="element-3729" data-mp="3189v44iq12l6">
                    {item.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{
                color: `${NAVY}b3`
              }} data-id="element-3730" data-mp="3a7dbh0qi73vz">
                    {item.text}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Mission */}
        <section style={{
        backgroundColor: NAVY
      }} data-id="element-3731" data-mp="j7whbo97l428">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center text-white" data-id="element-3732" data-mp="j73hl78o3etl">
            <h2 className="font-bebas text-3xl sm:text-4xl tracking-wider leading-tight mb-5" data-id="element-3733" data-mp="wy8fo4wdzz1t">
              {c.missionHeading}
            </h2>
            <p className="text-base sm:text-lg leading-relaxed text-white/80 mb-5" data-id="element-3734" data-mp="t02av2f3ztjj">
              {c.missionText}
            </p>
            <p className="font-bebas text-3xl tracking-wider" data-id="element-3735" data-mp="9k7e0ah3q5l">
              {c.missionClosing}
            </p>
          </div>
        </section>
      </main>

      <footer className="py-8 text-center bg-white border-t border-[#12336b]/10" data-id="element-3736" data-mp="e6p9jp72g4vk">
        <p className="text-xs uppercase tracking-[0.2em]" style={{
        color: `${NAVY}b3`
      }} data-id="element-3737" data-mp="985pxvfy356d">
          {c.footer}
        </p>
      </footer>
    </div>;
}